import { createClient } from '@supabase/supabase-js'
import { supabase } from './supabase'

export interface Client {
  id: string; profile_id: string | null; full_name: string; email: string; phone: string | null; company: string | null
  address: string | null; city: string | null; province: string | null; postal_code: string | null; website: string | null
  notes: string | null; type: string | null; source: string | null; language: string | null; budget_range: string | null; tags: string[] | null; created_at: string
}
export interface Project {
  id: string; client_id: string; title: string; description: string | null; status: string; shoot_date: string | null
  delivery_date: string | null; budget: number | null; notes: string | null; created_at: string
}
export interface Invoice {
  id: string; project_id: string; client_id: string; invoice_number: string | null; amount: number; tax_amount: number; subtotal: number
  tps_amount: number; tvq_amount: number; status: string; due_date: string | null; paid_date: string | null; notes: string | null
  payment_terms: string | null; created_at: string
}
export interface InvoiceItem { id?: string; invoice_id?: string; service_id: string | null; description: string; quantity: number; unit_price: number; amount: number; sort_order: number }
export interface Service { id: string; name: string; description: string | null; default_price: number; category: string | null; active: boolean }
export interface Meeting {
  id: string; client_id: string | null; project_id: string | null; title: string; description: string | null; meeting_date: string
  duration_minutes: number | null; meeting_url: string | null; platform: string | null; status: string | null; notes: string | null
}
export interface Video { id: string; project_id: string | null; client_id: string | null; title: string; description: string | null; file_name: string; file_path: string; review_status: string | null; created_at: string }
export interface Contract { id: string; project_id: string; client_id: string; title: string; filename: string; file_path: string; file_size: number | null; notes: string | null; sent_at: string | null; viewed_at: string | null; created_at: string }

export const CLIENT_TYPES = ['entreprise', 'particulier']
export const PROJECT_STATUSES = ['planifié', 'tournage', 'montage', 'révision', 'livré', 'annulé']
export const INVOICE_STATUSES = ['brouillon', 'envoyée', 'payée', 'en retard', 'annulée']
export const PAYMENT_TERMS: [string, string][] = [['reception', 'Payable à réception'], ['15', 'Net 15 jours'], ['30', 'Net 30 jours'], ['45', 'Net 45 jours']]
export const MEETING_STATUSES = ['planifié', 'terminé', 'annulé']
export const PLATFORMS = ['teams', 'zoom', 'meet', 'en personne', 'téléphone']
export const REVIEW_STATUSES: [string, string][] = [['en_attente', 'En attente'], ['en_revision', 'En révision'], ['approuve', 'Approuvée'], ['change_demande', 'Changements demandés']]
export const reviewLabel = (s: string | null) => REVIEW_STATUSES.find(r => r[0] === s)?.[1] || s || '—'

export const TPS = 0.05
export const TVQ = 0.09975
export function calcTotals(items: { quantity: number; unit_price: number }[]) {
  const subtotal = round(items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0))
  const tps = round(subtotal * TPS), tvq = round(subtotal * TVQ)
  return { subtotal, tps, tvq, total: round(subtotal + tps + tvq) }
}
const round = (n: number) => Math.round(n * 100) / 100

export const clientLabel = (c?: { company?: string | null; full_name?: string | null } | null) => c ? (c.company || c.full_name || '—') : '—'

/** Numéro de facture séquentiel par année : PM-2026-001 */
export async function nextInvoiceNumber() {
  const year = new Date().getFullYear()
  const { data } = await supabase.from('invoices').select('invoice_number').like('invoice_number', `PM-${year}-%`)
  const max = (data || []).reduce((m, r: any) => Math.max(m, parseInt((r.invoice_number || '').split('-').pop(), 10) || 0), 0)
  return `PM-${year}-${String(max + 1).padStart(3, '0')}`
}

/**
 * Invite un client : crée son compte (si absent) et lui envoie un lien magique par courriel.
 * Le déclencheur handle_new_user lie automatiquement le compte à la fiche client via le courriel.
 * On utilise un client Supabase séparé pour ne pas toucher à la session de l'admin.
 */
export async function inviteClient(email: string, full_name: string) {
  const authOnly = createClient(import.meta.env.VITE_SUPABASE_URL, import.meta.env.VITE_SUPABASE_ANON_KEY, {
    auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
  })
  const { error } = await authOnly.auth.signInWithOtp({
    email: email.trim().toLowerCase(),
    options: { shouldCreateUser: true, emailRedirectTo: `${window.location.origin}/mot-de-passe`, data: { full_name } },
  })
  if (error) throw new Error(error.message)
}

/** Libellé lisible de la cible d'un commentaire de revue ('*' = projet, '@photos:<dossier>' = série de photos). */
export const reviewTarget = (f: string) => f === '*' ? 'l’ensemble du projet' : f.startsWith('@photos:') ? `l’ensemble des photos${f.length > 8 && f !== '@photos:*' ? ` « ${f.slice(8)} »` : ''}` : f
